import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { useLanguage } from "../../i18n/LanguageContext";
import { track } from "../../lib/analytics";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import CareDateField, { coerceCareDate } from "./CareDateField";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const CATEGORIES = [
  "visit_note",
  "discharge_summary",
  "lab_result",
  "imaging_report",
  "prescription",
  "procedure",
  "immunization",
  "referral",
  "other",
];

export function HistoryConsentDialog({ open, onOpenChange, onAccepted }) {
  const { t, language } = useLanguage();
  const [text, setText] = useState("");
  const [version, setVersion] = useState("");
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChecked(false);
    let cancelled = false;
    axios
      .get(`${API}/history/consent`, { params: { locale: language } })
      .then((res) => {
        if (cancelled) return;
        setText(res.data?.data?.text || "");
        setVersion(res.data?.data?.text_version || "");
      })
      .catch(() => {
        if (!cancelled) toast.error(t("history.loadError"));
      });
    return () => {
      cancelled = true;
    };
  }, [open, language, t]);

  const accept = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/history/consent`, {
        text_version: version,
        platform: "web",
        locale: language,
      });
      onAccepted?.();
      onOpenChange(false);
    } catch {
      toast.error(t("history.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="history-consent-dialog">
        <DialogHeader>
          <DialogTitle>{t("history.consentTitle")}</DialogTitle>
          <DialogDescription className="sr-only">{t("history.consentTitle")}</DialogDescription>
        </DialogHeader>
        <p className="text-sm whitespace-pre-wrap">{text}</p>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            data-testid="history-consent-check"
          />
          <span>{t("history.consentAccept")}</span>
        </label>
        <Button onClick={accept} disabled={!checked || saving || !version} data-testid="history-consent-submit">
          {t("history.consentAccept")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

const DEFAULT_MAX_FILES = 8;

export default function AddRecordModal({
  open,
  onOpenChange,
  subjectId,
  onCreated,
  maxFiles,
}) {
  const { t } = useLanguage();
  const [mode, setMode] = useState("document");
  const [category, setCategory] = useState("lab_result");
  const [precision, setPrecision] = useState("day");
  const [eventDate, setEventDate] = useState("");
  const [unknownDate, setUnknownDate] = useState(false);
  const [title, setTitle] = useState("");
  const [facility, setFacility] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileLimit = Number.isInteger(maxFiles) && maxFiles > 0 ? maxFiles : DEFAULT_MAX_FILES;

  useEffect(() => {
    if (!open) return;
    setMode("document");
    setCategory("lab_result");
    setPrecision("day");
    setEventDate("");
    setUnknownDate(false);
    setTitle("");
    setFacility("");
    setNotes("");
    setFiles([]);
  }, [open]);

  const submit = async () => {
    if (!subjectId) return;
    setSaving(true);
    try {
      let response;
      const dateFields = unknownDate || !eventDate
        ? {}
        : { event_date: eventDate, event_date_precision: precision };
      if (mode === "entry") {
        response = await axios.post(`${API}/subjects/${subjectId}/records`, {
          category,
          title,
          facility: facility || null,
          notes: notes || null,
          ...dateFields,
        });
      } else {
        const form = new FormData();
        files.forEach((item) => form.append("file", item));
        form.append("category", category);
        if (!unknownDate && eventDate) {
          form.append("event_date", eventDate);
          form.append("event_date_precision", precision);
        }
        if (title) form.append("title", title);
        if (facility) form.append("facility", facility);
        if (notes) form.append("notes", notes);
        response = await axios.post(`${API}/subjects/${subjectId}/records`, form);
      }
      const data = response.data?.data;
      if (data?.duplicate_warning) toast.warning(t("history.duplicateWarning"));
      else toast.success(t("history.saved"));
      track("history_record_added", {
        kind: data?.kind,
        category: data?.category,
        source: data?.source,
      });
      onCreated?.();
      onOpenChange(false);
    } catch (error) {
      const code = error.response?.data?.error?.code;
      if (code === "history_storage_full") toast.error(t("history.storageFull"));
      else if (code === "history_consent_required") toast.error(t("history.consentRequired"));
      else if (code === "too_many_files") {
        toast.error(t("history.tooManyFiles").replace("{max}", String(fileLimit)));
      }
      else toast.error(t("history.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-record-modal">
        <DialogHeader>
          <DialogTitle>{t("history.addRecord")}</DialogTitle>
          <DialogDescription className="sr-only">{t("history.addRecord")}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant={mode === "document" ? "default" : "outline"}
            onClick={() => setMode("document")}
          >
            {t("history.uploadDocument")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === "entry" ? "default" : "outline"}
            onClick={() => setMode("entry")}
          >
            {t("history.addEntry")}
          </Button>
        </div>
        {mode === "document" && (
          <div className="space-y-1.5">
            <Label>{t("history.uploadDocument")}</Label>
            <Input
              type="file"
              multiple
              data-testid="history-file-input"
              onChange={(event) => {
                const picked = Array.from(event.target.files || []);
                if (picked.length > fileLimit) {
                  toast.error(t("history.tooManyFiles").replace("{max}", String(fileLimit)));
                  setFiles(picked.slice(0, fileLimit));
                } else {
                  setFiles(picked);
                }
              }}
            />
            {files.length > 0 && (
              <div className="space-y-1" data-testid="history-file-count">
                <p className="text-xs text-muted-foreground">
                  {t("history.filesSelected").replace("{count}", String(files.length))}
                </p>
                <ul className="text-xs text-muted-foreground space-y-0.5">
                  {files.map((item, index) => (
                    <li key={`${item.name}-${item.size}-${index}`} className="truncate">
                      {item.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <div className="space-y-1.5">
          <Label>{t("history.category")}</Label>
          <select
            className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="history-category"
          >
            {CATEGORIES.map((key) => (
              <option key={key} value={key}>
                {t(`history.category.${key}`)}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={unknownDate}
            onChange={(e) => setUnknownDate(e.target.checked)}
          />
          {t("history.dateUnknown")}
        </label>
        {!unknownDate && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("history.datePrecision")}</Label>
              <select
                className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                value={precision}
                onChange={(e) => {
                  const next = e.target.value;
                  setPrecision(next);
                  setEventDate((current) => coerceCareDate(current, next));
                }}
              >
                {["day", "month", "year"].map((key) => (
                  <option key={key} value={key}>
                    {t(`history.precision.${key}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("history.eventDate")}</Label>
              <CareDateField
                precision={precision}
                value={eventDate}
                onChange={setEventDate}
                testId="history-event-date"
              />
            </div>
          </div>
        )}
        <div className="space-y-1.5">
          <Label>{t("history.titleField")}</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="history-title-input" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("history.facility")}</Label>
          <Input value={facility} onChange={(e) => setFacility(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>{mode === "entry" ? t("history.details") : t("history.notes")}</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("history.cancel")}
          </Button>
          <Button
            onClick={submit}
            disabled={
              saving
              || (mode === "entry" && !title.trim())
              || (mode === "document" && files.length === 0)
            }
            data-testid="history-save-record"
          >
            {t("history.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
